import { TextFieldProps } from '@mui/material/TextField';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import SimpleAutoComplete from 'components/SimpleAutoComplete';
import { HOUR_IN_MILLISECONDS } from 'config';
import { i18n } from 'i18n';
import {
  PourPresetDto,
  useGetPourPreset,
  useGetPourPresetGroup
} from 'providers/api';
import React from 'react';

interface PourPresetFieldProps {
  pourPresetGroupId: string;
  pourPresetId?: string;
  touched?: boolean;
  error?: string
  disabledOptions?: string[];
  disabled?: boolean;
  textFieldVariant?: TextFieldProps['variant'];
  required?: boolean;
  autoFocus?: boolean;
  onChange: (pourPreset: PourPresetDto | null) => void;
  onBlur?: () => void;
}

const PourPresetField = ({
  pourPresetGroupId,
  pourPresetId,
  touched,
  error, disabled,
  textFieldVariant = 'outlined',
  required,
  autoFocus,
  disabledOptions = [],
  onBlur,
  onChange,
}: PourPresetFieldProps) => {
  const [open, setOpen] = React.useState(autoFocus ?? false);
  const [value, setValue] = React.useState<PourPresetDto | null>(null);
  const [searchTerm, setSearchTerm] = React.useState<string | null>(null);

  const { data: pourPreset, isFetched } = useGetPourPreset(
    pourPresetId!,
    { enabled: !!pourPresetId && !value },
  );
  const initialData = isFetched ? pourPreset : null;

  /**
   * If there is an initial value set it
   */
  React.useEffect(() => {
    setValue(initialData ?? null);
  }, [initialData]);

  const {
    data,
    isFetching,
  } = useGetPourPresetGroup(pourPresetGroupId, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const getDisabledOptions = (pourPresetOption: PourPresetDto) => disabledOptions.includes(pourPresetOption.entityId);
  const getOptionLabel = (pourPresetLabel: PourPresetDto) => pourPresetLabel.name ?? '';
  const getOptionSelected = (pourPresetSelection: PourPresetDto, groupValue: PourPresetDto) => pourPresetSelection.entityId === groupValue.entityId;

  const filterOptions = (pourPresetFilterOptions: PourPresetDto[], state: FilterOptionsState<PourPresetDto>) => pourPresetFilterOptions
    .filter((pourPresetFilterOption) => pourPresetFilterOption.name?.toLocaleLowerCase().includes(state.inputValue.toLocaleLowerCase()) ?? false);

  const noOptionsText = searchTerm ? i18n._('No pour presets matching').concat(` "${searchTerm}"`) : i18n._('No pour presets available');
  const handleOnChange = (newPourPreset: PourPresetDto | null) => {
    setValue(newPourPreset);
    onChange(newPourPreset);
  };

  /**
   * Default options with initial data if available
   */
  const options = data?.pourPresets ?? (initialData ? [initialData!] : []);

  return (
    <SimpleAutoComplete
      label={i18n._('Pour Preset')}
      value={value}
      autoFocus={autoFocus}
      disabled={disabled}
      options={options}
      loading={isFetching}
      getOptionDisabled={getDisabledOptions}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      onChange={handleOnChange}
      onSearch={(term: string) => setSearchTerm(term)}
      onBlur={onBlur}
      touched={touched}
      error={error}
      required={required}
      open={open}
      onOpen={(isOpen: boolean) => setOpen(isOpen)}
      textFieldVariant={textFieldVariant}
    />
  );
};

export default PourPresetField;
